import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Shield, Eye, AlertTriangle, Settings as SettingsIcon, Sparkles, DollarSign, Bell, User } from 'lucide-react';
import StripeOnboardingCard from '@/components/stripe/StripeOnboardingCard';
import UserBalanceWidget from '@/components/balance/UserBalanceWidget';
import { fetchUserProfile, updateUserMatureContentSettings } from '@/services/profile-service';
import { BlockedUsersList } from '@/components/settings/BlockedUsersList';
import { DiscordLinkSection } from '@/components/settings/DiscordLinkSection';
import { useBentoConfig } from '@/hooks/use-bento-config';
import PageLayout from '@/components/layout/PageLayout';
import { Input } from '@/components/ui/input';
import { useSignature } from '@/hooks/use-signature';
import { useUserRoles } from '@/hooks/use-user-roles';
import { ImageUpload } from '@/components/shared/ImageUpload';
import { uploadImage, UploadProgress } from '@/utils/imageHandling';
import { StorageManager } from '@/utils/storage-manager';
import { profileUpdateSchema, safeParse } from '@/lib/validation/schemas';

const Settings = () => {
  const { user, refreshSession } = useAuth();
  const queryClient = useQueryClient();
  const { isAdmin, isPremium, isPartner } = useUserRoles();
  
  // All hooks must be called at the top, before any conditional logic
  const { data: signature } = useSignature(user?.id);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress | null>(null);
  const [signatureImage, setSignatureImage] = useState<File | null>(null);
  
  // Bento profile functionality
  const { enableBentoProfile, isEnabling } = useBentoConfig(user?.id || '');
  
  // Check if bento profile is already enabled
  const { data: bentoConfig } = useQuery({
    queryKey: ['bento-config', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data } = await supabase
        .from('profile_2_configs')
        .select('*')
        .eq('user_id', user.id)
        .single();
      return data;
    },
    enabled: !!user?.id
  });
  
  // Fetch user profile
  const { data: profile, isLoading } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      return fetchUserProfile(user.id);
    },
    enabled: !!user?.id
  });
  
  // Handle age verification and mature content toggle
  const updateProfileMutation = useMutation({
    mutationFn: async ({ field, value }: { field: string, value: any }) => {
      if (!user?.id) throw new Error('User not authenticated');
      
      const updateData: Record<string, any> = {};
      updateData[field] = value;
      
      // If verifying age, set the timestamp
      if (field === 'age_verified_at' && value === true) {
        updateData.age_verified_at = new Date().toISOString();
      }
      
      // SECURITY: Validate profile updates (skip validation for boolean flags)
      if (!['age_verified_at', 'mature_content_enabled', 'profile_discoverable', 'mini_profile_enabled'].includes(field)) {
        const validationResult = safeParse(profileUpdateSchema.partial(), updateData);
        if (!validationResult.success) {
          const error = validationResult as { success: false; error: string };
          throw new Error(error.error);
        }
        // Use validated data
        Object.assign(updateData, validationResult.data);
      }
      
      const { error } = await supabase
        .from('profiles')
        .update(updateData)
        .eq('id', user.id);
      
      if (error) throw error;
      
      return { success: true };
    },
    onSuccess: () => {
      // Invalidate all profile-related queries to ensure fresh data
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      queryClient.invalidateQueries({ queryKey: ['public-profile'] });
      queryClient.invalidateQueries({ queryKey: ['infinite-bulletins'] });
      toast.success('Settings updated successfully');
      refreshSession();
    },
    onError: (error) => {
      console.error('Failed to update settings:', error);
      toast.error('Failed to update settings');
    }
  });
  
  const handleAgeVerificationChange = (checked: boolean) => {
    if (checked) {
      // User is verifying their age - use the dedicated function
      updateUserMatureContentSettings(user!.id, false, true);
    } else {
      // User is revoking age verification - also disable mature content
      updateUserMatureContentSettings(user!.id, false, false);
    }
    
    // Immediately invalidate all relevant caches
    queryClient.invalidateQueries({ queryKey: ['profile'] });
    queryClient.invalidateQueries({ queryKey: ['public-profile'] });
    queryClient.invalidateQueries({ queryKey: ['infinite-bulletins'] });
    
    // Force a re-render by dispatching a custom event
    window.dispatchEvent(new StorageEvent('storage', {
      key: 'age_verified',
      newValue: checked ? 'true' : 'false'
    }));
    
    toast.success('Settings updated successfully');
    refreshSession();
  };
  
  const handleMatureContentChange = (checked: boolean) => {
    if (!profile?.age_verified_at) {
      toast.error('You must verify your age first');
      return;
    }
    
    updateUserMatureContentSettings(user!.id, checked, true);
    
    // Immediately invalidate all relevant caches
    queryClient.invalidateQueries({ queryKey: ['profile'] });
    queryClient.invalidateQueries({ queryKey: ['public-profile'] });
    queryClient.invalidateQueries({ queryKey: ['infinite-bulletins'] });
    
    // Force a re-render by dispatching a custom event
    window.dispatchEvent(new StorageEvent('storage', {
      key: 'allow_mature_content',
      newValue: checked ? 'true' : 'false'
    }));
    
    toast.success('Settings updated successfully');
    refreshSession();
  };
  
  if (isLoading) {
    return <div className="p-8 flex justify-center">Loading settings...</div>;
  }
  
  const isAgeVerified = !!profile?.age_verified_at;
  const isMatureContentEnabled = !!profile?.mature_content_enabled;
  const isPremiumOrAdmin = isAdmin || isPremium;
  const isAdminOrPartner = isAdmin || isPartner;
  
  const upsertSignature = useMutation({
    mutationFn: async (payload: { image_url: string; link_url?: string }) => {
      if (!user?.id) throw new Error('Not authenticated');
      if (!isPremiumOrAdmin) throw new Error('Premium required');
      const { error } = await supabase
        .from('profile_signatures')
        .upsert({
          user_id: user.id,
          image_url: payload.image_url,
          link_url: payload.link_url || null,
          enabled: true,
          updated_at: new Date().toISOString(),
        }, { onConflict: 'user_id' });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      queryClient.invalidateQueries({ queryKey: ['profile-signature', user?.id] });
      toast.success('Signature saved');
    },
    onError: (e: any) => {
      toast.error(e?.message || 'Failed to save signature');
    }
  });
  
  return (
    <PageLayout>
      <div className="container max-w-4xl mx-auto py-8 px-4">
        <div className="mb-8 flex items-center gap-2">
          <SettingsIcon className="h-6 w-6" />
          <h1 className="text-2xl font-bold">Settings</h1>
        </div>
        
        <Tabs defaultValue="general" className="w-full">
          <TabsList className="inline-flex h-10 items-center justify-center rounded-md bg-muted p-1 text-muted-foreground mb-8">
            <TabsTrigger value="general" className="flex items-center gap-1.5">
              <User className="h-4 w-4" />
              General
            </TabsTrigger>
            <TabsTrigger value="profile" className="flex items-center gap-1.5">
              <Sparkles className="h-4 w-4" />
              Profile
            </TabsTrigger>
            <TabsTrigger value="notifications" className="flex items-center gap-1.5">
              <Bell className="h-4 w-4" />
              Notifications
            </TabsTrigger>
            {isAdminOrPartner && (
              <TabsTrigger value="payouts" className="flex items-center gap-1.5">
                <DollarSign className="h-4 w-4" />
                Payouts
              </TabsTrigger>
            )}
            <TabsTrigger value="privacy" className="flex items-center gap-1.5">
              <Shield className="h-4 w-4" />
              Privacy
            </TabsTrigger>
          </TabsList>

          {/* General Tab */}
          <TabsContent value="general" className="space-y-8">
          {/* Content Preferences Section */}
          <div className="bg-black/20 backdrop-blur-sm p-6 rounded-xl border border-white/10">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <Eye className="h-5 w-5" />
              Content Preferences
            </h2>
            
            <div className="space-y-6">
              {/* Age Verification */}
              <div className="flex flex-col space-y-2">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label className="text-base font-medium">Age Verification</Label>
                    <p className="text-sm text-muted-foreground">
                      Confirm you are at least 18 years old
                    </p>
                  </div>
                  <Switch 
                    checked={isAgeVerified}
                    onCheckedChange={handleAgeVerificationChange}
                  />
                </div>
                
                {!isAgeVerified && (
                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-md p-3 mt-2 flex gap-2 items-start">
                    <AlertTriangle className="h-5 w-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm">
                      You must verify that you are at least 18 years old to view mature content.
                    </p>
                  </div>
                )}
              </div>
              
              {/* Mature Content Toggle */}
              <div className="flex flex-col space-y-2">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label className="text-base font-medium">Show Mature Content</Label>
                    <p className="text-sm text-muted-foreground">
                      Allow viewing of mature or sensitive content
                    </p>
                  </div>
                  <Switch 
                    checked={isMatureContentEnabled}
                    onCheckedChange={handleMatureContentChange}
                    disabled={!isAgeVerified}
                  />
                </div>
                
                {isAgeVerified && isMatureContentEnabled && (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-md p-3 mt-2 flex gap-2 items-start">
                    <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm">
                      You have enabled viewing of mature content. This may include sensitive or adult-oriented material.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
          </TabsContent>

          {/* Profile Tab */}
          <TabsContent value="profile" className="space-y-8">
          {/* Profile Settings Section */}
          <div className="bg-black/20 backdrop-blur-sm p-6 rounded-xl border border-white/10">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <Sparkles className="h-5 w-5" />
              Profile Settings
            </h2>
            
            <div className="space-y-6">
              {/* Signature (Premium/Admin) */}
              <div className="space-y-3">
                <div className="space-y-0.5">
                  <h3 className="text-base font-medium">Signature (shown on your Loops posts)</h3>
                  <p className="text-sm text-muted-foreground">
                    Upload or link an image and an optional hyperlink. This appears under "From the artstream" on your Loops post pages for desktop viewers.
                  </p>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-2">
                    <ImageUpload
                      id="signature-image"
                      label="Signature Image"
                      type="banner"
                      preview={signature?.image_url || null}
                      onChange={(file) => setSignatureImage(file)}
                      uploadProgress={uploadProgress}
                      isPremiumUser={isPremiumOrAdmin}
                      className="w-full aspect-[16/9] h-auto min-h-[160px]"
                    />
                    <p className="text-xs text-muted-foreground">Recommended: 338×194 (upload higher-res like 1352×776)</p>
                  </div>
                  <div className="space-y-1">
                    <Label>Hyperlink (optional)</Label>
                    <Input
                      defaultValue={signature?.link_url || ''}
                      placeholder="https://your-site.com"
                      disabled={!isPremiumOrAdmin || upsertSignature.isPending}
                      onBlur={(e) => {
                        (e.currentTarget as any).dataset.pendingValue = e.currentTarget.value;
                      }}
                      data-field="link_url"
                    />
                  </div>
                </div>

                <div className="flex items-start justify-between gap-4">
                  <p className="text-xs text-muted-foreground">
                    Disclaimer: We reserve the right to disable this feature if it is abused. Links must comply with our community guidelines.
                  </p>
                  {isPremiumOrAdmin ? (
                    <Button
                      disabled={upsertSignature.isPending}
                      onClick={async () => {
                        const link = document.querySelector('[data-field="link_url"]') as HTMLInputElement | null;
                        const link_url = (link?.dataset.pendingValue ?? link?.value ?? '').toString().trim();
                        
                        // If we have a new signature image, upload it first
                        if (signatureImage) {
                          try {
                            console.log('Starting signature image upload:', { 
                              fileName: signatureImage.name,
                              fileSize: signatureImage.size,
                              fileType: signatureImage.type,
                              userId: user!.id 
                            });
                            
                            setUploadProgress({ progress: 0, state: 'uploading' });
                            
                            // Use uploadImage helper which handles all the details
                            const image_url = await uploadImage(
                              signatureImage,
                              user!.id,
                              'signature', // Store under distinct signature path to avoid banner overwrite
                              (progress) => setUploadProgress(progress)
                            );
                            
                            console.log('Image upload successful, got URL:', image_url);
                            
                            // Now update the database record
                            upsertSignature.mutate({ image_url, link_url });
                          } catch (error) {
                            console.error('Failed to upload signature image:', error);
                            toast.error(error instanceof Error ? error.message : 'Failed to upload signature image');
                            setUploadProgress({ progress: 0, state: 'error' });
                          }
                        } else if (signature?.image_url) {
                          // If no new image but we have an existing one, just update the link
                          console.log('Updating signature link only, using existing image:', signature.image_url);
                          upsertSignature.mutate({ 
                            image_url: signature.image_url, 
                            link_url 
                          });
                        } else {
                          toast.error('Signature image is required');
                        }
                      }}
                    >
                      Save Signature
                    </Button>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Button asChild>
                        <a href="/premium">Upgrade to Premium</a>
                      </Button>
                      <span className="text-xs text-muted-foreground">Premium or admin required to enable Signature.</span>
                    </div>
                  )}
                </div>
              </div>
              {/* Profile 2.0 Section */}
              <div className="space-y-3">
                <div className="space-y-0.5">
                  <h3 className="text-base font-medium">Profile 2.0 (Beta)</h3>
                  <p className="text-sm text-muted-foreground">
                    Enable the new bento-style profile with customizable widgets and layouts.
                  </p>
                </div>
                
                {bentoConfig?.is_enabled ? (
                  <div className="bg-green-500/10 border border-green-500/30 rounded-md p-3 flex gap-2 items-center">
                    <Sparkles className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <p className="text-sm">
                      Profile 2.0 is enabled! Visit your bento profile at{' '}
                      <a 
                        href={`/2/${profile?.username}`} 
                        className="text-primary hover:underline"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        /2/{profile?.username}
                      </a>
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Button 
                      onClick={() => enableBentoProfile()} 
                      disabled={true}
                      className="w-full sm:w-auto opacity-50 cursor-not-allowed"
                    >
                      Enable Profile 2.0
                    </Button>
                    <p className="text-xs text-muted-foreground">
                      Profile 2.0 is currently in development and temporarily disabled for new users.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications" className="space-y-8">
          {/* Notifications Settings Section */}
          <div className="bg-black/20 backdrop-blur-sm p-6 rounded-xl border border-white/10">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <Bell className="h-5 w-5" />
              Notifications
            </h2>
            
            <div className="space-y-6">
              {/* Discord Notifications Section */}
              <div className="space-y-3">
                <div className="space-y-0.5">
                  <h3 className="text-base font-medium">Discord Notifications</h3>
                  <p className="text-sm text-muted-foreground">
                    Link your Discord account to receive instant DM notifications for likes, comments, follows, and more.
                  </p>
                </div>
                <DiscordLinkSection />
              </div>
            </div>
          </div>
          </TabsContent>

          {/* Payouts Tab (Admin/Partner Only) */}
          {isAdminOrPartner && (
            <TabsContent value="payouts" className="space-y-8">
          {/* Payouts & Earnings Section */}
          <div className="bg-black/20 backdrop-blur-sm p-6 rounded-xl border border-white/10">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              Payouts & Earnings
            </h2>
            
            <div className="space-y-6">
              {/* Balance Widget */}
              <div className="space-y-3">
                <div className="space-y-0.5">
                  <h3 className="text-base font-medium">Your Balance</h3>
                  <p className="text-sm text-muted-foreground">
                    Track your earnings from drops and marketplace sales
                  </p>
                </div>
                <UserBalanceWidget />
              </div>
              
              {/* Stripe Onboarding */}
              <div className="space-y-3">
                <div className="space-y-0.5">
                  <h3 className="text-base font-medium">Payment Account</h3>
                  <p className="text-sm text-muted-foreground">
                    Connect your Stripe account to receive payouts
                  </p>
                </div>
                <StripeOnboardingCard />
              </div>
            </div>
          </div>
          </TabsContent>
          )}

          {/* Privacy Tab */}
          <TabsContent value="privacy" className="space-y-8">
          {/* Privacy Settings Section */}
          <div className="bg-black/20 backdrop-blur-sm p-6 rounded-xl border border-white/10">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Privacy Settings
            </h2>
            
            <div className="space-y-6">
              {/* Blocked Users Section */}
              <div className="space-y-3">
                <div className="space-y-0.5">
                  <h3 className="text-base font-medium">Blocked Users</h3>
                  <p className="text-sm text-muted-foreground">
                    Manage users you have blocked. Blocked users cannot see your content or interact with you.
                  </p>
                </div>
                <BlockedUsersList />
              </div>
            </div>
          </div>
          </TabsContent>
        </Tabs>
      </div>
    </PageLayout>
  );
};

export default Settings;
